import {
  fetchAllConsumptionAlerts,
  recordConsumptionAlert as postConsumptionAlert,
} from "../services/captionPublishingApi";

const STORAGE_KEY = "captionPublishingConsumptionAlerts";
const MAX_LOCAL_ENTRIES = 300;
const DEDUPE_WINDOW_MS = 60 * 60 * 1000;

const safeParse = (value) => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const buildConsumptionAlert = (user, options = {}) => ({
  alertId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  flaggedAt: new Date().toISOString(),
  email: user?.email || "",
  name: user?.name || "",
  googleId: user?.googleId || "",
  pictureUrl: user?.pictureUrl || "",
  reason: options.reason || "resource_limit",
  message: String(options.message || "").slice(0, 500),
  page: options.page || (typeof window !== "undefined" ? window.location.pathname : "/captionPublishing"),
  status: "open",
});

export const getLocalConsumptionAlerts = () => {
  if (typeof window === "undefined") {
    return [];
  }

  return safeParse(localStorage.getItem(STORAGE_KEY));
};

export const appendLocalConsumptionAlert = (alert) => {
  if (typeof window === "undefined" || !alert) {
    return alert;
  }

  const existing = getLocalConsumptionAlerts();
  const next = [alert, ...existing].slice(0, MAX_LOCAL_ENTRIES);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event("consumption-alerts-changed"));

  return alert;
};

const shouldSkipDuplicate = (existingAlerts, nextAlert) => {
  const nextTime = new Date(nextAlert.flaggedAt).getTime();

  return existingAlerts.some((entry) => {
    if (String(entry.email || "").toLowerCase() !== String(nextAlert.email || "").toLowerCase()) {
      return false;
    }

    if (entry.reason !== nextAlert.reason) {
      return false;
    }

    const entryTime = new Date(entry.flaggedAt).getTime();
    return Math.abs(nextTime - entryTime) < DEDUPE_WINDOW_MS;
  });
};

export const recordConsumptionAlert = async (user, options = {}) => {
  if (!user?.email) {
    return null;
  }

  const alert = buildConsumptionAlert(user, options);
  const existing = getLocalConsumptionAlerts();

  if (shouldSkipDuplicate(existing, alert)) {
    return null;
  }

  appendLocalConsumptionAlert(alert);

  try {
    await postConsumptionAlert(alert);
  } catch {
    // Keep local alerts until API route is deployed.
  }

  return alert;
};

export const mergeConsumptionAlerts = (remoteItems = [], localItems = []) => {
  const map = new Map();

  [...remoteItems, ...localItems].forEach((entry) => {
    const key = entry.alertId || `${entry.email}-${entry.flaggedAt}-${entry.reason}`;
    if (!map.has(key)) {
      map.set(key, entry);
    }
  });

  return Array.from(map.values()).sort(
    (left, right) => new Date(right.flaggedAt).getTime() - new Date(left.flaggedAt).getTime(),
  );
};

export const groupConsumptionAlertsByUser = (alerts = []) => {
  const grouped = new Map();

  alerts.forEach((alert) => {
    const email = String(alert.email || "").toLowerCase();
    if (!email) {
      return;
    }

    const existing = grouped.get(email);

    if (!existing) {
      grouped.set(email, { ...alert, hitCount: 1 });
      return;
    }

    const isNewer =
      new Date(alert.flaggedAt).getTime() > new Date(existing.flaggedAt).getTime();

    grouped.set(email, {
      ...(isNewer ? alert : existing),
      hitCount: existing.hitCount + 1,
    });
  });

  return Array.from(grouped.values()).sort(
    (left, right) => new Date(right.flaggedAt).getTime() - new Date(left.flaggedAt).getTime(),
  );
};

export const loadConsumptionAlertsForAdmin = async () => {
  const localItems = getLocalConsumptionAlerts();

  try {
    const remoteItems = await fetchAllConsumptionAlerts();
    return groupConsumptionAlertsByUser(mergeConsumptionAlerts(remoteItems, localItems));
  } catch {
    return groupConsumptionAlertsByUser(localItems);
  }
};
